import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  life: number;
  maxLife: number;
  amplitude: number;
  frequency: number;
  phase: number;
  baseX: number;
}

// Fondo animado de partículas subiendo con leve movimiento ondulatorio (canvas 2D).
export default function Particles({ count = 50 }: { count?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    const particles: Particle[] = [];
    let w = 0;
    let h = 0;
    let time = 0;

    const resize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      w = rect ? rect.width : window.innerWidth;
      h = rect ? rect.height : window.innerHeight;
      canvas.width = w;
      canvas.height = h;
    };

    const createParticle = (): Particle => {
      const baseX = Math.random() * w;
      return {
        x: baseX,
        y: h + Math.random() * 40 + 10,
        vx: 0,
        vy: -(Math.random() * 0.4 + 0.25),
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.3 + 0.08,
        life: 0,
        maxLife: Math.random() * 500 + 400,
        amplitude: Math.random() * 25 + 8,
        frequency: Math.random() * 0.006 + 0.002,
        phase: Math.random() * Math.PI * 2,
        baseX,
      };
    };

    resize();
    for (let i = 0; i < count; i++) {
      const p = createParticle();
      p.y = Math.random() * h; // arranca ya distribuidas en pantalla, no todas desde abajo
      p.life = Math.floor(Math.random() * p.maxLife);
      particles.push(p);
    }

    window.addEventListener('resize', resize);

    const animate = () => {
      ctx.clearRect(0, 0, w, h);
      time++;

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.y += p.vy;
        p.x = p.baseX + Math.sin(time * p.frequency + p.phase) * p.amplitude;
        p.life++;

        // fade in/out en el 8-12% inicial/final de su vida, opacidad plena en medio
        const lifeRatio = p.life / p.maxLife;
        const fade = lifeRatio < 0.08 ? lifeRatio * 12.5 : lifeRatio > 0.88 ? (1 - lifeRatio) * 8.33 : 1;
        const alpha = p.opacity * fade;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);

        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
        gradient.addColorStop(0, `oklch(0.68 0.22 142 / ${alpha * 0.8})`);
        gradient.addColorStop(0.5, `oklch(0.65 0.20 142 / ${alpha * 0.3})`);
        gradient.addColorStop(1, `oklch(0.65 0.20 142 / 0)`);

        ctx.fillStyle = gradient;
        ctx.fill();

        if (p.life >= p.maxLife || p.y < -20) {
          const np = createParticle();
          np.y = h + 20;
          particles[i] = np; // recicla el slot en vez de hacer push/splice
        }
      }

      animId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, [count]);

  return <canvas ref={canvasRef} className="absolute inset-0 z-[1] pointer-events-none" style={{ opacity: 0.7 }} />;
}